import type { InputHTMLAttributes } from 'react';
import React from 'react';

import { ErrorMessage } from 'src/app/components/lib/error-message';
import { Features } from 'src/app/state/types/loc-profile';
import { useFeatureCheck } from 'src/app/state/user-dispensaries';
import { omit } from 'src/app/utils/omit';

import { FormControl } from '../form-control';
import { Label } from '../label';

import { RadioCheckedIcon } from './checked-icon';
import { StyledRadio, StyledRadioGroup, StyledRadioFormControlLabel, RadioContainer } from './radio.styles';
import { RadioUncheckedIcon } from './unchecked-icon';

import type { RadioGroupProps as MUIRadioGroupProps, RadioProps } from '@mui/material';

type CustomRadioProps = RadioProps & {
  automationId?: string;
  errorMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & {
    'data-testid'?: string;
  };
  label?: string;
  tooltip?: string;
  useV2Tooltip?: boolean;
};

export function Radio(props: CustomRadioProps) {
  const { className, label, ...other } = props;
  return (
    <RadioContainer>
      <StyledRadioFormControlLabel
        className={className}
        control={
          <StyledRadio
            {...omit(other, ['errorMessage', 'automationId'])}
            checked={other.checked}
            checkedIcon={<RadioCheckedIcon />}
            icon={<RadioUncheckedIcon />}
          />
        }
        label={label}
      />
    </RadioContainer>
  );
}

export function RadioV2Tooltip(props: CustomRadioProps) {
  const isLoyaltyMarketingSubscriber = useFeatureCheck(Features.LoyaltyAndMarketingSubscriber);
  const useV2TooltipForTiers = isLoyaltyMarketingSubscriber;

  const { label, tooltip, useV2Tooltip = false, ...other } = props;
  return (
    <RadioContainer>
      <StyledRadioFormControlLabel
        control={
          <StyledRadio
            {...omit(other, ['errorMessage', 'automationId'])}
            checked={other.checked}
            checkedIcon={<RadioCheckedIcon />}
            icon={<RadioUncheckedIcon />}
          />
        }
        label={
          useV2TooltipForTiers
            ? label && (
                <Label id='radio-group-label_string' tooltip={tooltip} useV2Tooltip={useV2Tooltip}>
                  {label}
                </Label>
              )
            : label
        }
      />
    </RadioContainer>
  );
}

export type RadioGroupProps = {
  $gridColumns?: string;
  automationId?: string;
  children?: React.ReactNode;
  className?: string;
  errorMessage?: string;
  label?: React.ReactNode;
  labelForId?: string;
  labelPlacement?: 'top';
  onChange?: (event) => void;
  required?: boolean;
  tooltip?: React.ReactNode;
  useV2Tooltip?: boolean;
  value?: MUIRadioGroupProps['value'];
  /**
   * The boolean variant should only be used when the options of the radio group are 'Yes' and 'No'
   */
  variant?: 'boolean' | 'booleanString' | 'default';
};

export function RadioGroup(props: RadioGroupProps) {
  const {
    label,
    labelForId = '',
    labelPlacement,
    errorMessage,
    required,
    tooltip,
    children,
    variant = 'default',
    className = '',
    $gridColumns,
    automationId,
    useV2Tooltip = false,
  } = props;

  return (
    <FormControl
      $labelPlacement={label ? labelPlacement : undefined}
      automationId={automationId ?? 'radio-group-form-control'}
      className={className}
      gridColumns={$gridColumns}
      id={`form-control-radio-group_${typeof label === 'string' ? label : labelForId}`}
    >
      {label && (
        <Label
          id={`radio-group-label_${typeof label === 'string' ? label : labelForId}`}
          required={required}
          tooltip={tooltip}
          useV2Tooltip={useV2Tooltip}
        >
          {label}
        </Label>
      )}
      <StyledRadioGroup
        {...omit(props, ['errorMessage', 'automationId', 'labelPlacement', 'labelForId'])}
        $labelPlacement={label ? labelPlacement : undefined}
        $variant={variant}
      >
        {children}
      </StyledRadioGroup>
      {errorMessage && (
        <ErrorMessage $fullWidth={!label} error>
          {errorMessage}
        </ErrorMessage>
      )}
    </FormControl>
  );
}
